import React from 'react';
import styled from 'styled-components';
import { useInjection } from 'inversify-react';
import type {
  NavigationControlProps,
  INavigationControllerFactory,
  NavigationButtonsProps,
  INavigationController
} from './interfaces';
import { TYPES } from './types';
import { NavigationsDirection } from './NavigationController';
import { ReactComponent as ZoomOutIcon } from '../../assets/icons/navigationControl/zoom-out.svg';
import { ReactComponent as ZoomInIcon } from '../../assets/icons/navigationControl/zoom-in.svg';
import { ReactComponent as RotateUpIcon } from '../../assets/icons/navigationControl/r-up.svg';
import { ReactComponent as RotateRightIcon } from '../../assets/icons/navigationControl/r-right.svg';
import { ReactComponent as RotateDownIcon } from '../../assets/icons/navigationControl/r-down.svg';
import { ReactComponent as RotateLeftIcon } from '../../assets/icons/navigationControl/r-left.svg';
import { ReactComponent as PanUpIcon } from '../../assets/icons/navigationControl/pan-up.svg';
import { ReactComponent as PanRightIcon } from '../../assets/icons/navigationControl/pan-right.svg';
import { ReactComponent as PanDownIcon } from '../../assets/icons/navigationControl/pan-down.svg';
import { ReactComponent as PanLeftIcon } from '../../assets/icons/navigationControl/pan-left.svg';
import { ReactComponent as ResetViewIcon } from '../../assets/icons/navigationControl/home.svg';
import { useEffect } from 'react';

export const NavigationControl: React.FC<NavigationControlProps> = ({
  descriptor,
  isMaximized,
  integrationApi,
  resetCameraStrategy
}) => {
  const navigationControllerFactory = useInjection<INavigationControllerFactory>(TYPES.INavigationControllerFactory);

  const [navigationController, setNavigationController] = React.useState<INavigationController | undefined>();

  useEffect(() => {
    const navigationController = navigationControllerFactory.createNavigationController(integrationApi);
    navigationController.setResetCameraStrategy(resetCameraStrategy);
    setNavigationController(navigationController);
  }, [resetCameraStrategy, navigationControllerFactory, integrationApi]);

  const rows = [
    [NavigationsDirection.ZoomOut, NavigationsDirection.ZoomIn],
    [NavigationsDirection.PanUp],
    [NavigationsDirection.RotateUp],
    [
      NavigationsDirection.PanLeft,
      NavigationsDirection.RotateLeft,
      NavigationsDirection.ResetCamera,
      NavigationsDirection.RotateRight,
      NavigationsDirection.PanRight
    ],
    [NavigationsDirection.RotateDown],
    [NavigationsDirection.PanDown]
  ];

  if (!navigationController) {
    return null;
  }

  return (
    <Container className={`navigation-control ${descriptor} ${isMaximized ? 'maximized' : 'minimized'}`}>
      {rows.map((row, index) => {
        return (
          <ControlRow key={index}>
            {row.map((item, rindex) => {
              return (
                <NavigationButton
                  onInteractionStarted={(event: React.PointerEvent<HTMLElement>): void => {
                    if (event.button === 0) {
                      navigationController.onInteractionStarted(item);
                      (event.target as Element).setPointerCapture(event.pointerId);
                    }
                  }}
                  onInteractionEnded={(event: React.PointerEvent<HTMLElement>): void => {
                    navigationController.onInteractionEnded();
                    (event.target as Element).releasePointerCapture(event.pointerId);
                  }}
                  key={`${item}-${rindex}-${descriptor}`}
                  type={item}
                />
              );
            })}
          </ControlRow>
        );
      })}
    </Container>
  );
};

const NavigationButton: React.FC<NavigationButtonsProps> = (props) => {
  const { onInteractionStarted, onInteractionEnded, type } = props;
  type tIcons = Record<string, JSX.Element>;
  const icons: tIcons = {
    'zoom-out': <ZoomOutIcon />,
    'zoom-in': <ZoomInIcon />,
    'pan-up': <PanUpIcon />,
    'rotate-up': <RotateUpIcon />,
    'pan-left': <PanLeftIcon />,
    'rotate-left': <RotateLeftIcon />,
    'reset-camera': <ResetViewIcon />,
    'reset-scale': <ResetViewIcon />,
    'rotate-right': <RotateRightIcon />,
    'pan-right': <PanRightIcon />,
    'rotate-down': <RotateDownIcon />,
    'pan-down': <PanDownIcon />
  };
  const className = type.split('-')[0] + ' ' + type;
  return (
    <ControlItem onPointerDown={onInteractionStarted} onPointerUp={onInteractionEnded} className={className}>
      {icons[type]}
    </ControlItem>
  );
};

const Container = styled.div`
  position: absolute;
  right: 1em;
  top: 1em;
  display: flex;
  flex-direction: column;
  z-index: 1;
  overflow: hidden;
  pointer-events: none;

  & .rotate {
    padding: 0.1em;
  }
  & .reset-camera {
    transition: top 0s;
  }
  & .zoom {
    top: 1.1em;
    position: absolute;
    transition: top 0s;

    &.zoom-in {
      right: 0.9em;
    }

    &.zoom-out {
      left: 0.9em;
    }
  }

  &.minimized {
    top: 0;
    right: calc(1.6em + 10px); // Compensate the border to the side in other viewports
  }

  &.coronalView,
  &.axialView,
  &.sagittalView {
    right: 2em;

    &.minimized {
      display: none;
      .image-viewport-wrapper:hover &,
      &:hover {
        display: flex;
      }
    }
  }

  &.minimized,
  &.coronalView,
  &.axialView,
  &.sagittalView {
    width: 10.5em;
    height: 2em;

    .rotate,
    .pan {
      display: none;
    }
    .zoom,
    .reset-camera,
    .reset-scale {
      top: 0.25em;
      position: absolute;
    }
    .reset-camera {
      right: 2.3em;
    }

    .zoom-out {
      left: 2em;
    }
  }
`;

const ControlRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  pointer-events: none;
`;

const ControlItem = styled.div`
  width: 1.3em;
  height: 1.3em;
  padding: 0.3em;
  background-color: transparent;
  opacity: 0.8;
  filter: drop-shadow(0 0 2px rgba(0, 0, 0, 0.5));
  text-align: center;
  font-size: 1.5rem;
  display: flex;
  border-radius: 0.75em;
  fill: #fafafa;
  cursor: pointer;
  transition: all 0.1s;
  transform-origin: center;
  pointer-events: auto;

  &:hover {
    opacity: 1;
    transform: scale(1.05);
    filter: drop-shadow(0 0 4px rgba(0, 0, 0, 0.5));
  }
`;

export default NavigationControl;
